"use client";

import { NextIntlClientProvider, type AbstractIntlMessages } from "next-intl";
import { useRouter } from "next/navigation";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { locales, type Locale } from "@/i18n/routing";

type IntlProviderProps = {
  children: ReactNode;
  locale: string;
  messages: AbstractIntlMessages;
  now: Date;
  timeZone: string;
};

/**
 * Los textos de cada idioma, cada uno en su trozo: solo los baja quien cambia
 * de idioma sin recargar (~72 KB comprimidos cada uno). Rutas fijas y no una
 * plantilla, para que cualquier empaquetador los parta igual.
 */
const CARGAR_MENSAJES: Record<Locale, () => Promise<AbstractIntlMessages>> = {
  es: () => import("@/messages/es.json").then((modulo) => modulo.default),
  en: () => import("@/messages/en.json").then((modulo) => modulo.default),
  de: () => import("@/messages/de.json").then((modulo) => modulo.default),
};

function esLocale(valor: string): valor is Locale {
  return (locales as readonly string[]).includes(valor);
}

export interface IdiomaApp {
  /** El idioma en que se ve la app ahora mismo. */
  locale: string;
  /** Baja los textos de `locale` sin cambiar nada: para tenerlos ya al confirmar. */
  precargar: (locale: string) => Promise<void>;
  /**
   * Cambia el idioma de toda la app sin recargar la página. `despues` (avanzar
   * de pantalla, por ejemplo) se llama en el mismo render que los textos
   * nuevos. Si los textos no llegan, `router.refresh()`: el servidor vuelve a
   * pintar con el idioma de la cookie, como antes.
   */
  cambiar: (locale: string, despues?: () => void) => Promise<void>;
}

const ContextoIdioma = createContext<IdiomaApp | null>(null);

/** Cambiar el idioma en el cliente (spec 028 §6.5). Solo dentro de `IntlProvider` (el layout raíz). */
export function useIdiomaApp(): IdiomaApp {
  const idioma = useContext(ContextoIdioma);
  if (!idioma) throw new Error("useIdiomaApp: fuera de IntlProvider.");
  return idioma;
}

/**
 * Los textos de la app para los Client Components (`useTranslations`), con el
 * idioma que resuelve el proxy.
 *
 * Desde la spec 028 también cambia de idioma **sin recargar**: `cambiar`
 * importa los textos del idioma nuevo, los pone en el proveedor y ajusta
 * `document.documentElement.lang`. Lo que pinta el servidor en la ruta (el
 * `<title>`) se corrige en la siguiente navegación.
 *
 * Si el layout raíz vuelve a pintarse con otros props (un `router.refresh()`,
 * una navegación de servidor), mandan los props: el servidor ya lee el idioma
 * guardado y la cookie.
 */
export function IntlProvider({ children, locale, messages, now, timeZone }: IntlProviderProps) {
  const router = useRouter();
  // El idioma elegido en el cliente y los props sobre los que se eligió.
  const [propio, setPropio] = useState<{
    locale: Locale;
    messages: AbstractIntlMessages;
    base: AbstractIntlMessages;
  } | null>(null);
  const cargas = useRef<Map<Locale, Promise<AbstractIntlMessages>>>(new Map());

  const vigente = propio !== null && propio.base === messages ? propio : null;
  const localeActivo = vigente?.locale ?? locale;
  const mensajesActivos = vigente?.messages ?? messages;

  const traer = useCallback((destino: Locale) => {
    let carga = cargas.current.get(destino);
    if (!carga) {
      carga = CARGAR_MENSAJES[destino]();
      cargas.current.set(destino, carga);
      // Si falla, la próxima vez se intenta de nuevo.
      carga.catch(() => cargas.current.delete(destino));
    }
    return carga;
  }, []);

  const precargar = useCallback(
    async (destino: string) => {
      if (!esLocale(destino) || destino === locale) return;
      await traer(destino).then(
        () => undefined,
        () => undefined,
      );
    },
    [locale, traer],
  );

  const cambiar = useCallback(
    async (destino: string, despues?: () => void) => {
      if (!esLocale(destino) || destino === localeActivo) {
        despues?.();
        return;
      }
      // Volver al idioma del servidor: sus textos ya están en los props.
      if (destino === locale) {
        setPropio(null);
        document.documentElement.lang = destino;
        despues?.();
        return;
      }
      let textos: AbstractIntlMessages;
      try {
        textos = await traer(destino);
      } catch {
        despues?.();
        router.refresh();
        return;
      }
      setPropio({ locale: destino, messages: textos, base: messages });
      document.documentElement.lang = destino;
      despues?.();
    },
    [locale, localeActivo, messages, router, traer],
  );

  const idioma = useMemo<IdiomaApp>(
    () => ({ locale: localeActivo, precargar, cambiar }),
    [localeActivo, precargar, cambiar],
  );

  return (
    <ContextoIdioma value={idioma}>
      <NextIntlClientProvider locale={localeActivo} messages={mensajesActivos} now={now} timeZone={timeZone}>
        {children}
      </NextIntlClientProvider>
    </ContextoIdioma>
  );
}
